import React from 'react';
import { Outlet } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Divider
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Logout, 
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';

const drawerWidth = 280;

const getMenuIcon = (path: string) => {
  const iconMap: Record<string, React.ReactElement> = {
    '/dashboard': <DashboardIcon />,
    '/companies': <BusinessIcon />,
    '/users': <PeopleIcon />,
    '/services': <DescriptionIcon />,
    '/agreements': <AssignmentIcon />,
    '/transactions': <ReceiptIcon />,
    '/wallets': <WalletIcon />,
  };
  return iconMap[path] || <DashboardIcon />;
};

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getMenuItems = () => {
    const isClient = user?.role === 'client';
    const isCsm = user?.role === 'csm';
    const isAdmin = user?.role === 'admin';
    const canSeeWallets = isAdmin || user?.role === 'ae' || user?.role === 'employee';

    if (isCsm) {
      return [{ text: 'Transactions', path: '/transactions' }];
    }

    const allItems: Array<{ text: string; path: string; adminOnly?: boolean; staffOnly?: boolean }> = [
      { text: 'Companies', path: '/companies' },
      { text: 'Users', path: '/users' },
      { text: 'Services', path: '/services' },
      { text: 'Agreements', path: '/agreements' },
      { text: 'Transactions', path: '/transactions' },
      { text: 'Wallets', path: '/wallets', staffOnly: true },
    ];

    if (isClient) {
      return allItems.filter(item =>
        item.text !== 'Users' && item.text !== 'Services' && !item.adminOnly && !item.staffOnly
      );
    }
    return allItems.filter(item =>
      (!item.adminOnly || isAdmin) && (!item.staffOnly || canSeeWallets)
    );
  };

  const menuItems = getMenuItems();

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar
        sx={{
          background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
          color: '#ffffff',
          minHeight: '80px !important',
          display: 'flex',
          alignItems: 'center',
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          >
            <BusinessIcon sx={{ fontSize: 24, color: '#ffffff' }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontWeight: 700, 
                fontSize: '1.125rem', 
                lineHeight: 1.2,
                color: '#ffffff',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              }}
            >
              Billing
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                opacity: 0.95, 
                fontSize: '0.75rem',
                color: '#ffffff',
                fontWeight: 500,
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              }}
            >
              Dashboard
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflow: 'auto', py: 2 }}>
        <List sx={{ px: 1 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  py: 1.25,
                  mx: 1,
                  borderRadius: 2,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: location.pathname === item.path ? 'inherit' : 'text.secondary',
                  }}
                >
                  {getMenuIcon(item.path)}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.9375rem',
                    fontWeight: location.pathname === item.path ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: '72px !important', px: 3 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              color: 'text.primary',
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                borderRadius: 2,
                px: 2.5,
                py: '10px',
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor: '#2563eb',
                fontWeight: 600,
                fontSize: '0.9375rem',
                color: '#2563eb',
                backgroundColor: 'transparent',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 'fit-content',
                boxSizing: 'border-box',
                lineHeight: 1.75,
                letterSpacing: '0.02857em',
                textTransform: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderWidth: 2,
                  background: 'rgba(37, 99, 235, 0.04)',
                },
              }}
            >
              {user?.email || 'USER'}
            </Box>
            <Button
              variant="outlined"
              onClick={handleLogout}
              startIcon={<Logout />}
              sx={{
                borderRadius: 2,
                px: 2.5,
                borderWidth: 2,
                fontWeight: 600,
                '&:hover': {
                  borderWidth: 2,
                  background: 'rgba(37, 99, 235, 0.04)',
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 4 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          background: '#f1f5f9',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;